/**
 * ForgotPasswordPage - Request password reset email.
 *
 * Features:
 * - Email input for password reset
 * - Success message after submission
 * - Link back to login page
 */

import { useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { Input } from '@/common/components/input'
import { Button } from '@/common/components/button'
import { Text } from '@/common/components/text'
import { forgotPassword } from '@/common/services/auth'
import * as styles from './login.css'

export const Route = createFileRoute({
  component: ForgotPasswordPage,
})

function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      await forgotPassword(email)
      setIsSuccess(true)
    } catch (err) {
      // Don't reveal if email exists or not for security
      setIsSuccess(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSuccess) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <img
            src="/images/orchard-offers.svg"
            alt="Orchard Cash Offer"
            className={styles.logo}
          />

          <div className={styles.form}>
            <Text variant="bodySm" color="muted" align="center" className={styles.description}>
              If an account exists for <strong>{email}</strong>, you will receive a password reset email shortly.
            </Text>

            <Link to="/login">
              <Button type="button" width="full">
                Back to Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <img
          src="/images/orchard-offers.svg"
          alt="Orchard Cash Offer"
          className={styles.logo}
        />

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && (
            <Text variant="bodySm" color="error" align="center" className={styles.error}>
              {error}
            </Text>
          )}

          <Text variant="bodySm" color="muted" align="center" className={styles.description}>
            Enter your email address and we'll send you a link to reset your password.
          </Text>

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
            error={!!error}
            autoComplete="email"
          />

          <Button
            type="submit"
            width="full"
            disabled={isSubmitting || !email}
          >
            {isSubmitting ? 'Sending...' : 'Send Reset Link'}
          </Button>

          <Link to="/login" className={styles.backLink}>
            Back to Sign In
          </Link>
        </form>
      </div>
    </div>
  )
}
